// Dependencies
import React from 'react';
import {
  ElementInteraction,
  ElementImpression,
} from '@nerdwallet/nw-client-lib/tracking';

// Utils
import { cdnUri } from '@nerdwallet/nw-client-lib/cdn';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { zipWith } from 'lodash';
import { Box, Tiles, Type, Card } from '@nerdwallet/currency';

// Local Components
import CityResultsError from '../shared/city-results-error/city-results-error';
import { CompareDataProps } from '../shared/types';

import style from './city-suggestions.module.less';

type CitySuggestionsProps = {
  handleInputChange: (data: Record<string, any>) => void;
  sourceCityData: Record<string, any>;
  suggestedCityData: Record<string, any>[];
  suggestedCityCompareData: CompareDataProps[];
};

const imgUri = cdnUri('img/diy/col/city_photos', 'png');

const CitySuggestions = ({
  handleInputChange,
  sourceCityData,
  suggestedCityData,
  suggestedCityCompareData,
}: CitySuggestionsProps): JSX.Element => {
  const onSuggestionClick = (destinationCity) => {
    return (e) => {
      e.preventDefault();
      e.stopPropagation();

      const data = {
        inputs: {
          destinationCity,
        },
        name: 'costOfLivingCalculator',
      };
      handleInputChange(data);
    };
  };

  const renderSuggestions = () => {
    const sourceSlug = sourceCityData?.slug;

    return zipWith(
      suggestedCityData,
      suggestedCityCompareData,
      (city, compareData) => {
        const { id, photo, slug, name } = city;
        const { adjustedIncome } = compareData;
        const link = `/cost-of-living-calculator/compare/${sourceSlug}-vs-${slug}`;

        return (
          <Box key={id}>
            <ElementImpression
              id={`${id}`}
              elementName={`city-suggestion-${slug}`}
            >
              <ElementInteraction
                elementDomId={id}
                elementName={`city-suggestion-${slug}`}
                type="CLICK"
              >
                <Card
                  primary
                  href={link}
                  prompt=""
                  onClick={onSuggestionClick(id)}
                  className={style.card}
                >
                  <img className={style.img} alt="" src={imgUri(photo)} />
                  <Box paddingY={2}>
                    <Type bold align="center">
                      {name}
                    </Type>
                    <Type align="center">
                      Comparable Income: {adjustedIncome}
                    </Type>
                  </Box>
                </Card>
              </ElementInteraction>
            </ElementImpression>
          </Box>
        );
      }
    );
  };

  // The percent difference is calculated using the cities composite_index
  // If percent difference is NaN, this means we didn't receive the city data
  if (Number.isNaN(suggestedCityCompareData[0]?.colPercentDifference)) {
    // not on results page, so only update source city id
    const onRefreshClick = () => {
      const newData = {
        inputs: {
          sourceCity: sourceCityData.id,
        },
        name: 'costOfLivingCalculator',
      };
      handleInputChange(newData);
    };

    return <CityResultsError onRefreshClick={onRefreshClick} />;
  }

  return (
    <Box padding={{ desktop: 3 }} paddingTop={{ mobile: 3 }}>
      <Tiles columns={{ mobile: 1, desktop: 3 }}>{renderSuggestions()}</Tiles>
    </Box>
  );
};

CitySuggestions.displayName = 'CitySuggestions';

export default CitySuggestions;
